import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Lock, Plane } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * ContentGate — shows first `visibleCount` children fully,
 * then fades/blurs the rest with a sign-up prompt.
 *
 * Props:
 *  - user: current auth user (null if guest)
 *  - visibleCount: how many items to show freely (default 3)
 *  - title: CTA heading
 *  - message: CTA sub-message
 *  - onSignUp: callback when CTA button clicked
 */
export default function ContentGate({
  children,
  user,
  visibleCount = 3,
  title = 'Join Fly N Out to see more',
  message = 'Create a free account to access the full community feed, reviews, and traveler tips.',
  onSignUp,
}) {
  if (user) return <>{children}</>;

  const items = React.Children.toArray(children);
  const visible = items.slice(0, visibleCount);
  const preview = items.slice(visibleCount, visibleCount + 2);
  const hasMore = items.length > visibleCount;

  return (
    <div className="space-y-3">
      {/* Fully visible items */}
      {visible}

      {/* Blurred preview + overlay */}
      {hasMore && (
        <div className="relative">
          {/* Blurred preview items */}
          <div className="pointer-events-none select-none" aria-hidden>
            <div className="space-y-3 opacity-60 blur-sm">
              {preview}
            </div>
          </div>

          {/* Gradient overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, transparent 0%, var(--background) 60%)',
            }}
          />

          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="absolute bottom-0 left-0 right-0 flex justify-center pb-4"
          >
            <Card
              className="fly-card p-5 mx-4 w-full max-w-md text-center shadow-xl border-[#FF4500]/20"
              data-testid="content-gate-card"
            >
              <div className="flex justify-center mb-3">
                <div className="w-10 h-10 rounded-full bg-[#FF4500]/10 flex items-center justify-center">
                  <Plane className="w-5 h-5 text-[#FF4500]" />
                </div>
              </div>
              <h3
                className="font-bold text-base mb-1"
                style={{ fontFamily: 'Space Grotesk' }}
              >
                {title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">{message}</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  className="fly-btn-primary"
                  onClick={onSignUp}
                  data-testid="content-gate-signup-btn"
                >
                  Join Free
                </Button>
                <Button variant="outline" onClick={onSignUp}>
                  Log In
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Extra spacer so CTA doesn't overlap content below */}
          <div className="h-32" />
        </div>
      )}
    </div>
  );
}
